import { CommonModule } from '@angular/common';
import { Component, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import type { EChartsOption } from 'echarts';
import { NGX_ECHARTS_CONFIG, NgxEchartsModule } from 'ngx-echarts';
import { AccordionModule } from 'primeng/accordion';
import { DialogModule } from 'primeng/dialog';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonComponent } from "src/app/@core/components/ui/button/button.component";
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { IconComponent } from "src/app/@core/components/ui/icon/icon.component";
import { CustomMenuItem, MenuComponent } from 'src/app/@core/components/ui/menu/menu.component';
import { MultiSelectComponent } from 'src/app/@core/components/ui/multi-select/multi-select.component';
import { MultiSelectConfig, MultiSelectOption } from 'src/app/@core/components/ui/multi-select/multi-select.model';
import { PopoverComponent } from 'src/app/@core/components/ui/popover/popover.component';
import { ToggleComponent } from 'src/app/@core/components/ui/toggle/toggle.component';
import { SocketService } from 'src/app/@core/services/socket.service';
import { ToastService } from 'src/app/@core/services/toast.service';
import { DataTableComponent } from 'src/app/@shared/components/data-table/data-table.component';
import { TableColumn, TableConfig } from 'src/app/@shared/models/table.models';
import { GEOTECHNICAL_ANALYSIS_SVG, SVG } from 'src/app/common/svgs';
import { GeoTechnicalAnalysisReportDataService } from '../../../../services/geo-technical-analysis-report-data.service';
import {
  GeotechnicalAnalysisService,
  IAlternateLeaderboardItem,
  IAlternateLeaderboardStatus,
  ICalculateCostsForNewAlternateLayerMethod,
  IDashboardFilters,
  ILayerVisualizationCell,
  ILayerVisualizationData,
  ILayerVisualizationStabilizationMethod,
  ILeaderboardFilters,
  IStabilizationMethodCell,
} from '../../../../services/geotechnical-analysis.service';

// Chart color palette for work items
const CHART_COLORS = [
  '#4472C4', '#ED7D31', '#A9D18E', '#FF6B6B', '#5B9BD5',
  '#FF9F43', '#54A0FF', '#5F27CD', '#00D2D3', '#1DD1A1',
  '#C0392B', '#8E44AD', '#2980B9', '#16A085', '#F39C12',
];

// Loading: stacked bar (3-color segments — mirrors Material/Labor/Equipment stacked charts)
const CHART_LOADING_STACKED_BAR: EChartsOption = {
  graphic: {
    elements: [{
      type: 'group',
      left: 'center',
      top: 'center',
      children: new Array(5).fill(0).flatMap((_, i) =>
        (['#7780FF1A', '#278DEB1A', '#FF6A2A1A'] as string[]).map((color, j) => ({
          type: 'rect',
          x: i * 26,
          shape: { x: 0, y: -(j * 26 + 26), width: 16, height: 24 },
          style: { fill: color },
          keyframeAnimation: {
            duration: 1000,
            delay: i * 180 + j * 60,
            loop: true,
            keyframes: [
              { percent: 0.5, scaleY: 0.2, easing: 'cubicIn' },
              { percent: 1, scaleY: 1, easing: 'cubicOut' },
            ],
          },
        }))
      ),
    }],
  },
};
// Loading: line chart — dots arranged in a wave shape that pulse in/out
const _LINE_OFFSETS = [0, -22, 12, -32, -8, 18, -4] as number[];
const CHART_LOADING_LINE: EChartsOption = {
  graphic: {
    elements: [{
      type: 'group',
      left: 'center',
      top: 'center',
      children: [
        // Connecting polyline
        {
          type: 'polyline',
          shape: { points: _LINE_OFFSETS.map((y, i) => [i * 22, y]) },
          style: { stroke: '#12B76A1A', lineWidth: 2, fill: 'none' },
          keyframeAnimation: {
            duration: 1100,
            loop: true,
            keyframes: [
              { percent: 0.5, style: { opacity: 0.3 } as any, easing: 'cubicIn' },
              { percent: 1, style: { opacity: 1 } as any, easing: 'cubicOut' },
            ],
          },
        } as any,
        // Animated dots on top
        ..._LINE_OFFSETS.map((yOffset, i) => ({
          type: 'circle',
          x: i * 22,
          y: yOffset,
          shape: { cx: 0, cy: 0, r: 5 },
          style: { fill: '#12B76A1A' },
          keyframeAnimation: {
            duration: 1100,
            delay: i * 160,
            loop: true,
            keyframes: [
              { percent: 0.5, scaleX: 0.25, scaleY: 0.25, easing: 'cubicIn' },
              { percent: 1, scaleX: 1, scaleY: 1, easing: 'cubicOut' },
            ],
          },
        })),
      ],
    }],
  },
};
// Loading: simple bar — bars of varying heights (mirrors single-series bar charts)
const CHART_LOADING_BAR: EChartsOption = {
  graphic: {
    elements: [{
      type: 'group',
      left: 'center',
      top: 'center',
      children: ([60, 35, 75, 45, 55, 30] as number[]).map((h, i) => ({
        type: 'rect',
        x: i * 24,
        shape: { x: 0, y: -h, width: 16, height: h },
        style: { fill: '#7780FF1A' },
        keyframeAnimation: {
          duration: 900 + (i % 3) * 150,
          delay: i * 130,
          loop: true,
          keyframes: [
            { percent: 0.5, scaleY: 0.15, easing: 'cubicIn' },
            { percent: 1, scaleY: 1, easing: 'cubicOut' },
          ],
        },
      })),
    }],
  },
};
// Loading: mixed positive/negative bars — mirrors schedule impact which can go below zero
const CHART_LOADING_BAR_MIXED: EChartsOption = {
  graphic: {
    elements: [{
      type: 'group',
      left: 'center',
      top: 'center',
      children: (
        [
          { h: 50, neg: false },
          { h: 30, neg: true },
          { h: 65, neg: false },
          { h: 45, neg: true },
          { h: 40, neg: false },
          { h: 55, neg: true },
        ] as { h: number; neg: boolean }[]
      ).map(({ h, neg }, i) => ({
        type: 'rect',
        x: i * 24,
        shape: { x: 0, y: neg ? 0 : -h, width: 16, height: h },
        style: { fill: neg ? '#FF6A2A1A' : '#12B76A1A' },
        keyframeAnimation: {
          duration: 900 + (i % 3) * 150,
          delay: i * 130,
          loop: true,
          keyframes: [
            { percent: 0.5, scaleY: 0.15, easing: 'cubicIn' },
            { percent: 1, scaleY: 1, easing: 'cubicOut' },
          ],
        },
      })),
    }],
  },
};


@Component({
  selector: 'app-geo-technical-dashboard',
  standalone: true,
  imports: [
    IconComponent, ButtonComponent, CommonModule, TooltipModule,
    DialogModule, FormsModule,
    PopoverComponent, RadioButtonModule,
    MenuComponent, NgxEchartsModule, ToggleComponent, SkeletonModule, DataTableComponent, AccordionModule, DialogComponent, MultiSelectComponent
  ],
  providers: [
    { provide: NGX_ECHARTS_CONFIG, useValue: { echarts: () => import('echarts') } }
  ],
  templateUrl: './geo-technical-dashboard.component.html',
  styleUrl: './geo-technical-dashboard.component.scss'
})
export class GeoTechnicalDashboardComponent implements OnInit, OnDestroy {

  GEOTECHNICAL_ANALYSIS_SVG = GEOTECHNICAL_ANALYSIS_SVG;
  SVG = SVG;

  // Alternate Leaderboard
  alternateOptions: CustomMenuItem[] = [];
  selectedAlternateKey: string = '';
  leaderboardDataMap: Record<string, IAlternateLeaderboardItem> = {};
  leaderboardLoading = false;

  get selectedAlternateLabel(): string {
    return this.alternateOptions.find(o => o.value === this.selectedAlternateKey)?.label ?? 'Select Alternate';
  }

  private readonly defaultLeaderboardData: IAlternateLeaderboardItem = {
    key: '',
    totalCost: { currency: 'USD', value: 0 },
    costMix: { material: 0, equipment: 0, labor: 0 },
    scheduleImpact: 0,
    scheduleChangePercent: 0,
    generalWorksCost: 0,
    stabilizationWorksCost: 0,
    totalUnitCost: 0,
    generalWorksBreakdown: [],
    stabilizationWorksBreakdown: [],
  };

  get selectedLeaderboardData(): IAlternateLeaderboardItem {
    return this.leaderboardDataMap[this.selectedAlternateKey] ?? this.defaultLeaderboardData;
  }

  // Resource cost breakdown tabs (populated from vendor analysis)
  generalWorksTabs: string[] = [];
  stabilizationTabs: string[] = [];
  selectedGeneralWorksTab = '';
  selectedStabilizationTab = '';
  alternatives: IAlternateLeaderboardItem[] = [];

  // Resource cost breakdown view toggle
  resourceCostView: 'card' | 'table' = 'card';

  readonly resourceCostTableConfig: TableConfig = { paginator: false };

  get resourceCostTableColumns(): TableColumn[] {
    return [
      { field: 'alternate', header: 'Alternate', type: 'text' },
      { field: 'materialCost', header: 'Material Cost ($)', type: 'number', pipeArgs: ['1.0-2'] },
      { field: 'equipmentCost', header: 'Equipment Cost ($)', type: 'number', pipeArgs: ['1.0-2'] },
      { field: 'laborCost', header: 'Labor Cost ($)', type: 'number', pipeArgs: ['1.0-2'] },
      { field: 'totalWorkCost', header: 'Total Work Cost ($)', type: 'number', pipeArgs: ['1.0-2'] },
      { field: 'projectArea', header: 'Project Area (SY)', type: 'text' },
      { field: 'unitCost', header: 'Unit Cost ($ / unit)', type: 'number', pipeArgs: ['1.0-2'] },
    ];
  }

  get resourceCostTableData(): Record<string, any>[] {
    const isGeneral = !!this.selectedGeneralWorksTab;
    const selectedTab = isGeneral ? this.selectedGeneralWorksTab : this.selectedStabilizationTab;
    const projectAreaValue = this.geoReportDataService.vendorAnalysis()?.projectInfo?.projectArea?.value ?? null;
    const projectArea = projectAreaValue ?? '-';
    return this.alternatesData.map(alt => {
      const breakdown = isGeneral ? alt.generalWorksBreakdown : alt.stabilizationWorksBreakdown;
      const item = (breakdown ?? []).find(w => w.workName === selectedTab);
      const material = item?.material ?? 0;
      const equipment = item?.equipment ?? 0;
      const labor = item?.labor ?? 0;
      const totalWorkCost = material + equipment + labor;
      const unitCost = projectAreaValue && projectAreaValue > 0 ? Math.round((totalWorkCost / projectAreaValue) * 100) / 100 : 0;
      return { alternate: alt.key, materialCost: material, equipmentCost: equipment, laborCost: labor, totalWorkCost, projectArea, unitCost };
    });
  }

  // Work cost breakdown view toggle
  workCostView: 'card' | 'table' = 'card';

  // Alternates data stored for table view
  alternatesData: IAlternateLeaderboardItem[] = [];

  readonly workCostTableConfig: TableConfig = { paginator: false };

  get workCostTableColumns(): TableColumn[] {
    const cols: TableColumn[] = [
      { field: 'alternate', header: 'Alternate', type: 'text' },
    ];
    for (const name of this.generalWorksTabs) {
      cols.push({ field: name, header: name, type: 'number', pipeArgs: ['1.0-2'] });
    }
    for (const name of this.stabilizationTabs) {
      cols.push({ field: name, header: name, type: 'number', pipeArgs: ['1.0-2'] });
    }
    cols.push(
      { field: 'totalCost', header: 'Total Cost ($)', type: 'number', pipeArgs: ['1.0-2'] },
      { field: 'projectArea', header: 'Project Area (SY)', type: 'number', pipeArgs: ['1.0-0'] },
      { field: 'unitCost', header: 'Unit Cost ($ / unit)', type: 'number', pipeArgs: ['1.0-2'] },
    );
    return cols;
  }

  get workCostTableData(): Record<string, any>[] {
    const projectArea = this.geoReportDataService.vendorAnalysis()?.projectInfo?.projectArea?.value ?? null;
    return this.alternatesData.map(alt => {
      const row: Record<string, any> = {
        alternate: alt.key,
        totalCost: alt.totalCost.value,
        projectArea,
        unitCost: alt.totalUnitCost,
      };
      for (const name of this.generalWorksTabs) {
        row[name] = alt.generalWorksBreakdown?.find(w => w.workName === name)?.cost ?? 0;
      }
      for (const name of this.stabilizationTabs) {
        row[name] = alt.stabilizationWorksBreakdown?.find(w => w.workName === name)?.cost ?? 0;
      }
      return row;
    });
  }

  // Charts (mutable – rebuilt from real data)
  costBreakdownChartOption: EChartsOption = CHART_LOADING_STACKED_BAR;
  unitCostByAlternatesChartOption: EChartsOption = CHART_LOADING_LINE;
  totalCostAcrossAlternatesChartOption: EChartsOption = CHART_LOADING_STACKED_BAR;
  unitCostAcrossAlternatesChartOption: EChartsOption = CHART_LOADING_BAR;

  // Layer Visualization
  layerVisualizationData: ILayerVisualizationData | null = null;
  layerVisualizationLoading = true;

  // Fixed-position tooltip for layer cells (avoids table overflow clipping)
  layerTooltip: {
    layerName: string;
    depth: string;
    stabilizationMethods: ILayerVisualizationStabilizationMethod[];
    x: number;
    y: number;
  } | null = null;

  onLayerCellEnter(event: MouseEvent, cell: ILayerVisualizationCell, layerName: string = ''): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.layerTooltip = {
      layerName,
      depth: cell.depth,
      stabilizationMethods: cell.stabilizationMethods,
      x: rect.left + rect.width / 2,
      y: rect.bottom + 10,
    };
  }

  onLayerCellLeave(): void {
    this.layerTooltip = null;
  }

  private readonly ALTERNATE_BADGE_COLORS = [
    { bg: '#FEF3C7', text: '#92400E' },
    { bg: '#D1FAE5', text: '#065F46' },
    { bg: '#DBEAFE', text: '#1E40AF' },
    { bg: '#EDE9FE', text: '#5B21B6' },
    { bg: '#FCE7F3', text: '#9D174D' },
    { bg: '#FEE2E2', text: '#991B1B' },
  ];

  getAlternateBgColor(index: number): string {
    return this.ALTERNATE_BADGE_COLORS[index % this.ALTERNATE_BADGE_COLORS.length].bg;
  }

  getAlternateTextColor(index: number): string {
    return this.ALTERNATE_BADGE_COLORS[index % this.ALTERNATE_BADGE_COLORS.length].text;
  }

  getLayerSvg(rowIndex: number): string {
    const key = `LAYER_${rowIndex + 1}`;
    return (GEOTECHNICAL_ANALYSIS_SVG.LAYER_VISUALIZATION as Record<string, string>)[key] ?? '';
  }

  /**
   * Returns true only when the depth string represents a positive numeric value.
   * Used in the template to suppress the cylinder, badge and method icon entirely
   * when a layer has zero or missing depth.
   */
  hasPositiveDepth(depth: string): boolean {
    return this.parseDepthNum(depth) > 0;
  }

  /**
   * Extracts the numeric depth span from a depth string.
   * Handles ranges ("0-5 ft", "2.5–7.5 m"), single values ("10 ft", "3 m", "8"),
   * and en-dash / em-dash separators.  Returns 0 when unparseable.
   */
  private parseDepthNum(depth: string): number {
    if (!depth) return 0;
    // Normalise en-dash / em-dash → hyphen
    const s = depth.replace(/[–—]/g, '-').trim();
    // Range: "0-5 ft", "2.5-7.5 m"
    const range = s.match(/([\d.]+)\s*-\s*([\d.]+)/);
    if (range) return Math.abs(parseFloat(range[2]) - parseFloat(range[1]));
    // Single value: "5 ft", "3 m", bare "8"
    const single = s.match(/([\d.]+)/);
    return single ? parseFloat(single[1]) : 0;
  }

  /**
   * Body height constants (controls the body section only, not total cylinder).
   *
   * MAX_BODY_PX: body height assigned to the deepest layer.  Increase to make
   *   cylinders taller overall; decrease to make them more compact.
   * MIN_BODY_PX: floor so depth-0 layers still show a thin visible wall sliver.
   */
  private readonly MAX_BODY_PX = 70;  // body px for the deepest layer in the dataset
  private readonly MIN_BODY_PX = 8;   // floor: always show a thin cylinder wall

  /**
   * Injected sanitizer — used to pre-sanitize SVG strings into SafeHtml once,
   * so the template never needs the sanitizeHtml pipe on every render.
   */
  private readonly sanitizer = inject(DomSanitizer);

  /**
   * Cached result of filteredLayerVisualizationData.
   * Recomputed only when layerVisualizationData or appliedFilters changes,
   * NOT on every change-detection cycle.
   */
  filteredLayerVisualizationData: ILayerVisualizationData | null = null;

  /**
   * Pre-sanitized SVG map: key = `${rowIndex}-${depth}`, value = SafeHtml.
   * Populated once via precomputeLayerSvgs() whenever filtered data changes.
   * Template does a simple Map.get() — zero computation per render cycle.
   */
  readonly layerSvgCache = new Map<string, SafeHtml>();

  /**
   * Recomputes the filtered layer-visualization data and immediately rebuilds
   * the SVG cache. Call this whenever layerVisualizationData or appliedFilters
   * changes so both the filtered view and SVG cache stay in sync.
   */
  private refreshLayerVisualization(): void {
    this.filteredLayerVisualizationData = this.computeFilteredLayerData();
    this.precomputeLayerSvgs();
  }

  /**
   * The filter logic previously in the `filteredLayerVisualizationData` getter.
   * Now a plain private method so it is only called when inputs actually change.
   */
  private computeFilteredLayerData(): ILayerVisualizationData | null {
    if (!this.layerVisualizationData) return null;

    let alternateKeys = this.layerVisualizationData.alternateKeys;
    const suitableAlternateKey = this.layerVisualizationData.suitableAlternateKey;
    let rows = this.layerVisualizationData.rows;

    if (this.appliedFilters.topN !== null) {
      const leaderboardKeys = new Set(this.alternateOptions.map(o => o.value as string));
      const keptIndices: number[] = [];
      alternateKeys = alternateKeys.filter((k, i) => {
        if (leaderboardKeys.has(k)) { keptIndices.push(i); return true; }
        return false;
      });
      rows = rows.map(row => ({
        ...row,
        cells: keptIndices.map(i => row.cells[i]).filter(Boolean),
      }));
    }

    if (this.appliedFilters.selectedAlternates.length > 0) {
      const selectedSet = new Set(this.appliedFilters.selectedAlternates);
      const keptIndices: number[] = [];
      alternateKeys = alternateKeys.filter((k, i) => {
        if (selectedSet.has(k)) { keptIndices.push(i); return true; }
        return false;
      });
      rows = rows.map(row => ({
        ...row,
        cells: keptIndices.map(i => row.cells[i]).filter(Boolean),
      }));
    }

    if (!this.appliedFilters.allLayersEnabled && this.appliedFilters.enabledLayers.length > 0) {
      const enabledSet = new Set(this.appliedFilters.enabledLayers);
      rows = rows.filter(row => enabledSet.has(row.layerName));
    }

    return { alternateKeys, suitableAlternateKey, rows };
  }

  /**
   * Eagerly generates and sanitizes every visible cylinder SVG, storing results
   * in layerSvgCache. Called once after filtered data changes — never during render.
   *
   * Key: `${rowIndex}-${depth}` — unique because rowIndex selects the layer asset
   * set and depth determines the body height. pxPerUnit is computed once from the
   * full filtered dataset before iterating, so all SVGs share a consistent scale.
   */
  private precomputeLayerSvgs(): void {
    this.layerSvgCache.clear();
    const data = this.filteredLayerVisualizationData;
    if (!data?.rows.length) return;

    // Compute pxPerUnit once for the whole dataset (same logic as old layerPxPerUnit getter)
    const maxDepth = Math.max(
      0,
      ...data.rows.flatMap(r => r.cells.map(c => this.parseDepthNum(c.depth))),
    );
    const pxPerUnit = maxDepth > 0 ? this.MAX_BODY_PX / maxDepth : 4;

    data.rows.forEach((row, rowIndex) => {
      const layerKey = Math.min(rowIndex, 2);
      row.cells.forEach(cell => {
        const cacheKey = `${rowIndex}-${cell.depth}`;
        if (this.layerSvgCache.has(cacheKey)) return; // same depth already computed for this row
        const bodyH = this.getLayerBodyHeight(cell.depth, pxPerUnit);
        const svgStr = this.buildSplitLayerSvg(bodyH, this.SPLIT_LAYERS[layerKey], rowIndex);
        // bypassSecurityTrustHtml is safe here: SVG is generated entirely from
        // internal constants and numeric values — no user input is interpolated.
        this.layerSvgCache.set(cacheKey, this.sanitizer.bypassSecurityTrustHtml(svgStr));
      });
    });
  }

  /**
   * Dataset-relative px-per-depth-unit — kept for external callers if any,
   * but is no longer called on every render cycle (precomputeLayerSvgs uses it once).
   */
  private get layerPxPerUnit(): number {
    const data = this.filteredLayerVisualizationData;
    if (!data?.rows.length) return 4;
    const maxDepth = Math.max(
      0,
      ...data.rows.flatMap(r => r.cells.map(c => this.parseDepthNum(c.depth))),
    );
    return maxDepth > 0 ? this.MAX_BODY_PX / maxDepth : 4;
  }

  /**
   * Body height in px for a given depth string, clamped to [MIN_BODY_PX, MAX_BODY_PX].
   * Returns 0 for depth ≤ 0 so the cylinder collapses to just its top+bottom caps
   * (a flat disc) instead of showing a fake minimum wall for zero-depth layers.
   */
  private getLayerBodyHeight(depth: string, pxPerUnit: number): number {
    const v = this.parseDepthNum(depth);
    if (v <= 0) return 0; // zero depth → flat disc, no body section
    return Math.min(this.MAX_BODY_PX, Math.max(this.MIN_BODY_PX, Math.round(v * pxPerUnit)));
  }

  /**
   * Purpose-built split SVG parts for every layer.
   * Each file has its own viewBox; all heights are used at exact float proportions
   * (no rounding) to eliminate sub-pixel letterboxing gaps.
   *
   * rowIndex % 3 → layer:
   *   0  Wearing / Asphalt   layer-1-{top,mid,bot}.svg
   *   1  Base    / Gravel    layer-2-{top,mid,bot}.svg
   *   2  Subgrade / Soil     layer-3-{top,mid,bot}.svg
   *
   * The mid.svg for each layer is designed for seamless vertical tiling —
   * its top and bottom edges match so no seam lines appear when the body
   * section is tiled to match the cell's depth value.
   */
  private readonly SPLIT_LAYERS: Record<number, {
    top: { src: string; w: number; h: number };
    mid: { src: string; w: number; h: number };
    bot: { src: string; w: number; h: number };
  }> = {
      0: { // Wearing / Asphalt
        top: { src: 'images/geo-layers/layer-1-top.svg', w: 1305, h: 298 },
        mid: { src: 'images/geo-layers/layer-1-mid.svg', w: 1305, h: 114 },
        bot: { src: 'images/geo-layers/layer-1-bot.svg', w: 1305, h: 190 },
      },
      1: { // Base / Gravel
        top: { src: 'images/geo-layers/layer-2-top.svg', w: 1424, h: 305 },
        mid: { src: 'images/geo-layers/layer-2-mid.svg', w: 1424, h: 114 },
        bot: { src: 'images/geo-layers/layer-2-bot.svg', w: 1424, h: 190 },
      },
      2: { // Subgrade / Soil
        top: { src: 'images/geo-layers/layer-3-top.svg', w: 1395, h: 305 },
        mid: { src: 'images/geo-layers/layer-3-mid.svg', w: 1395, h: 114 },
        bot: { src: 'images/geo-layers/layer-3-bot.svg', w: 1396, h: 191 },
      },
    };

  /**
   * Resolves a single method SVG path for a cell's stabilization methods.
   *
   * Resolves the method SVG asset for a cell's stabilization methods.
   *
   * Returns null (no SVG rendered) when:
   *   - the methods array is empty / null, OR
   *   - all method values are blank / whitespace-only strings, OR
   *   - the method name is not "geocell" or "geogrid"
   *
   * Matching order for non-blank values (case-insensitive regex):
   *   1. Any value contains "geocell"  → geocell.svg
   *   2. Any value contains "geogrid"  → geogrid.svg
   *   3. Anything else (e.g. "Lime Treated Clay") → null  (no SVG)
   */
  getMethodSvgSrc(methods: ILayerVisualizationStabilizationMethod[]): string | null {
    if (!methods?.length) return null;
    // Strip blank / whitespace-only values — a cell with no meaningful method gets no SVG.
    const values = methods.map(m => m.value?.trim() ?? '').filter(v => v.length > 0);
    if (!values.length || values.every(v => v === '-')) return null;
    if (values.some(v => /geocell/i.test(v))) return 'images/geo-layers/methods/geocell.svg';
    if (values.some(v => /geogrid/i.test(v))) return 'images/geo-layers/methods/geogrid.svg';
    // All other method names (e.g. "Lime Treated Clay") have no SVG illustration.
    return 'images/geo-layers/methods/generic-method.svg';
  }

  /**
   * Builds a cylinder SVG for layers that have dedicated top / middle / bottom files.
   *
   * The body is filled using a single SVG <pattern> element rather than stacked
   * <svg> stamps.  This eliminates the sub-pixel white-line gaps that appeared
   * between adjacent stamp elements due to integer rounding of each tile's height.
   *
   * The <pattern> uses exact (unrounded) tile dimensions so the browser can tile
   * with sub-pixel precision.  Because mid.svg is designed for seamless tiling
   * (its top and bottom edges match), no seam lines are visible either.
   *
   * Method icons are NOT embedded here — they are rendered as a separate
   * <img> element above the cylinder in the template (see getMethodSvgSrc).
   *
   * @param bodyH    — desired body section height in px (dataset-relative)
   * @param parts    — top / mid / bot SVG descriptors
   * @param rowIndex — used to create a unique pattern id per row (avoids collisions)
   */
  private buildSplitLayerSvg(
    bodyH: number,
    parts: typeof this.SPLIT_LAYERS[number],
    rowIndex: number,
  ): string {
    const W = 112;

    // Use EXACT float heights (no Math.round) for every section.
    // Each layer's top-cap height has a fractional component (e.g. 25.57 px for
    // layer 1, 24.47 px for layer 3).  When the browser rounds adjacent float
    // boundaries differently (one up, one down) a sub-pixel transparent gap
    // appears between sections.  We fix this with a 1 px OVERLAP: the body
    // starts 1 px before the top cap ends, and the bottom cap starts 1 px
    // before the body ends.  Because later elements in SVG paint on top of
    // earlier ones, the 1-px overlap region is always covered by the section
    // drawn second — so the result is seamless with zero distortion.
    const OVERLAP = 1; // px

    const topH = parts.top.h / parts.top.w * W;   // e.g. 25.573 px  (top cap natural height)
    const midH = parts.mid.h / parts.mid.w * W;   // e.g.  9.770 px  (pattern tile height)
    const botH = parts.bot.h / parts.bot.w * W;   // e.g. 16.322 px  (bottom cap natural height)

    // Body rect starts OVERLAP px above where the top cap ends so it covers
    // any fractional-pixel gap at the top-cap / body boundary.
    const bodyStart = topH - OVERLAP;
    const bodyRectH = bodyH + OVERLAP; // extra px compensates for the upward shift

    // Bottom cap starts OVERLAP px above where the body naturally ends, again
    // covering any fractional-pixel gap.  The image uses xMidYMin meet so it
    // renders at its natural dimensions (top-aligned) and the tiny transparent
    // strip falls at the very bottom of the SVG — outside the visual content.
    const botStart = topH + bodyH - OVERLAP;
    const botImgH = botH + OVERLAP; // image viewport is 1 px taller to reach totalH

    const totalH = topH + bodyH + botH; // overall SVG height unchanged

    // Unique pattern id per row — prevents fill collisions across rows
    const patId = `slp-${rowIndex}`;

    // TOP CAP: rendered at exact natural height; no overlap needed at the top.
    const topSvg =
      `<image href="${parts.top.src}" x="0" y="0" ` +
      `width="${W}" height="${topH}" preserveAspectRatio="xMidYMid slice"/>`;

    // BODY: <pattern> tiles mid.svg seamlessly.  The rect starts 1 px early
    // (bodyStart) so it overlaps the tail end of the top cap, eliminating gaps.
    // Pattern origin matches the rect's top-left so tiling is aligned.
    const defsAndBody = bodyH > 0
      ? `<defs>` +
      `<pattern id="${patId}" x="0" y="${bodyStart}" ` +
      `width="${W}" height="${midH}" patternUnits="userSpaceOnUse">` +
      `<image href="${parts.mid.src}" x="0" y="0" ` +
      `width="${W}" height="${midH}" preserveAspectRatio="xMidYMid slice"/>` +
      `</pattern>` +
      `</defs>` +
      `<rect x="0" y="${bodyStart}" width="${W}" height="${bodyRectH}" fill="url(#${patId})"/>`
      : '';

    // BOTTOM CAP: starts 1 px early (botStart) to overlap the body's tail.
    // xMidYMin meet keeps the image at its natural size, top-aligned — the
    // 1-px margin at the bottom of the viewport is transparent and invisible.
    const bottomSvg =
      `<image href="${parts.bot.src}" x="0" y="${botStart}" ` +
      `width="${W}" height="${botImgH}" preserveAspectRatio="xMidYMin meet"/>`;

    return (
      `<svg xmlns="http://www.w3.org/2000/svg" ` +
      `width="${W}" height="${totalH}" style="display:block;">` +
      topSvg + defsAndBody + bottomSvg +
      `</svg>`
    );
  }

  /**
   * Renders a geo-layer cell as a pure-SVG cylinder.
   *
   * All three layers now use dedicated top / middle / bottom SVG files
   * (defined in SPLIT_LAYERS) so every layer benefits from:
   *   • Zero stretching  — middle tile is stamped at its natural height
   *   • Zero seam lines  — mid.svg edges are designed to match seamlessly
   *   • Zero gap lines   — 1-px section overlaps + exact float dimensions
   *
   * Method icons are rendered as a separate <img> above each cylinder
   * in the template using getMethodSvgSrc().
   *
   * Asset mapping (Math.min(rowIndex, 2)):
   *   0      → layer-1-{top,mid,bot}.svg  Wearing  / Asphalt
   *   1      → layer-2-{top,mid,bot}.svg  Base     / Gravel
   *   2, 3…  → layer-3-{top,mid,bot}.svg  Subgrade / Soil (clamped — never cycles back)
   */
  getLayerSvgWithHeight(rowIndex: number, depth: string): string {
    const pxPerUnit = this.layerPxPerUnit;
    const bodyH = this.getLayerBodyHeight(depth, pxPerUnit);
    // Clamp to 2 so any row beyond the 3rd always uses the Subgrade/layer-3 SVGs.
    // (rowIndex % 3 would cycle back to layer-1 for row 3, which is incorrect.)
    const layerKey = Math.min(rowIndex, 2); // 0, 1, or 2
    return this.buildSplitLayerSvg(bodyH, this.SPLIT_LAYERS[layerKey], rowIndex);
  }

  // Impact vs Alternate view toggle
  impactView: 'card' | 'table' = 'card';
  scheduleImpactChartOption: EChartsOption = CHART_LOADING_BAR_MIXED;
  costImpactChartOption: EChartsOption = CHART_LOADING_BAR;

  readonly impactTableConfig: TableConfig = { paginator: false };

  get impactTableColumns(): TableColumn[] {
    return [
      { field: 'alternate', header: 'Alternate', type: 'text' },
      { field: 'theoreticalBudget', header: 'Theoretical Budget ($)', type: 'number', pipeArgs: ['1.0-2'] },
      { field: 'alternateCost', header: 'Alternate Cost', type: 'number', pipeArgs: ['1.0-2'] },
      { field: 'costImpact', header: 'Cost Impact (%)', type: 'number', pipeArgs: ['1.0-2'] },
      { field: 'theoreticalSchedule', header: 'Theoretical Schedule', type: 'number', pipeArgs: ['1.0-2'] },
      { field: 'alternateSchedule', header: 'Alternate Schedule', type: 'number', pipeArgs: ['1.0-2'] },
      { field: 'scheduleImpact', header: 'Schedule Impact (%)', type: 'number', pipeArgs: ['1.0-2'] },
    ];
  }

  get impactTableData(): Record<string, any>[] {
    if (this.alternatesData.length === 0) return [];
    const theoreticalSchedule = this.geoReportDataService.vendorAnalysis()?.projectInfo?.theoreticalScheduleWorkdays?.value ?? null;
    const theoreticalBudget = this.geoReportDataService.vendorAnalysis()?.projectInfo?.theoreticalBudget?.value ?? null;
    const theoreticalScheduleValue = theoreticalSchedule ?? 0;
    const theoreticalBudgetValue = theoreticalBudget ?? 0;
    return this.alternatesData.map(alt => ({
      alternate: alt.key,
      theoreticalBudget,
      alternateCost: alt.totalCost.value,
      costImpact: theoreticalBudgetValue > 0
        ? Math.round(((alt.totalCost.value - theoreticalBudgetValue) / theoreticalBudgetValue) * 100 * 10) / 10
        : 0,
      theoreticalSchedule,
      alternateSchedule: alt.scheduleImpact,
      scheduleImpact: theoreticalScheduleValue > 0
        ? Math.round(((alt.scheduleImpact - theoreticalScheduleValue) / theoreticalScheduleValue) * 100 * 10) / 10
        : 0,
    }));
  }

  expandedDashboardTable: 'layerVisualization' | 'resourceCost' | 'workCost' | 'impact' | null = null;

  // Analysis Summary state has been moved to GeoTechnicalAnalysisSummaryTabComponent.


  filterRankBy: 'cost' | 'schedule' = 'cost';
  filterSelectedTopN: number | null = null;
  filterTopNOptions = [3, 5, 10];
  filterLayers: { label: string; enabled: boolean }[] = [];
  filterLayersLoading = false;
  filterAlternateOptions: MultiSelectOption[] = [];
  filterSelectedAlternates: string[] = [];

  readonly alternateMultiSelectConfig: MultiSelectConfig = {
    placeholder: 'All alternates',
    searchable: true,
    showClear: true,
    showToggleAll: true,
    display: 'chip',
    appendTo: null,
  };

  private rawLeaderboardAlternates: IAlternateLeaderboardItem[] = [];

  // New Alternate Dialog state
  showNewAlternateDialog = false;
  calculatingNewAlternate = signal(false);

  /** Per-layer accordion state for the "new alternate" dialog. */
  newAlternateDialogLayers: {
    layerName: string;
    open: boolean;
    works: {
      workName: string;
      open: boolean;
      selectedMethod: string;
      methods: { label: string; value: string }[];
    }[];
  }[] = [];

  private appliedFilters: { rankBy: 'cost' | 'schedule'; topN: number | null; allLayersEnabled: boolean; enabledLayers: string[]; selectedAlternates: string[] } = {
    rankBy: 'cost', topN: null, allLayersEnabled: true, enabledLayers: [], selectedAlternates: [],
  };


  constructor(
    private geoReportDataService: GeoTechnicalAnalysisReportDataService,
    private geotechnicalAnalysisService: GeotechnicalAnalysisService,
    private toast: ToastService,
    private socketService: SocketService,
  ) {
    // Populate filterLayers reactively whenever VE data arrives from the shared service
    effect(() => {
      const veData = this.geoReportDataService.valueEngineering();
      if (!veData?.alternatives) return;
      const layerSet = new Set<string>();
      for (const alt of veData.alternatives) {
        for (const v of (alt.values ?? [])) {
          if (v.layer) layerSet.add(v.layer);
        }
      }
      const layers = Array.from(layerSet);
      if (layers.length > 0) {
        const savedEnabledSet = this.appliedFilters.allLayersEnabled
          ? null
          : new Set(this.appliedFilters.enabledLayers);
        this.filterLayers = layers.map(l => ({
          label: l,
          enabled: savedEnabledSet ? savedEnabledSet.has(l) : true,
        }));
      }
      this.filterLayersLoading = false;
    });
  }

  get analysisId(): string {
    return this.geoReportDataService.analysisReportId();
  }

  get isViewer(): boolean {
    const p = this.geoReportDataService.permissions();
    return !this.geoReportDataService.isOwner() && !this.geoReportDataService.isEditor() && !p?.canComment;
  }

  ngOnInit(): void {
    this.loadSavedFiltersAndLeaderboard();
    this.loadLayersFromValueEngineering();
    this.loadVendorAnalysis();
    this.loadLayerVisualization();
  }

  private loadSavedFiltersAndLeaderboard(): void {
    if (!this.analysisId) {
      this.loadAlternateLeaderboard();
      return;
    }
    this.geotechnicalAnalysisService.getDashboardFilters(this.analysisId).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          const saved = res.data as IDashboardFilters;
          if (saved.rankBy) this.filterRankBy = saved.rankBy;
          if (saved.topN != null) this.filterSelectedTopN = saved.topN;
          const enabledLayers = saved.enabledLayers ?? [];
          const allEnabled = enabledLayers.length === 0;
          const selectedAlternates = saved.selectedAlternates ?? [];
          this.appliedFilters = {
            rankBy: this.filterRankBy,
            topN: this.filterSelectedTopN,
            allLayersEnabled: allEnabled,
            enabledLayers,
            selectedAlternates,
          };
          this.loadAlternateLeaderboard({
            rankBy: this.filterRankBy,
            topN: this.filterSelectedTopN,
            layers: allEnabled ? undefined : enabledLayers,
          });
        } else {
          this.loadAlternateLeaderboard();
        }
      },
      error: () => this.loadAlternateLeaderboard(),
    });
  }

  ngOnDestroy(): void { }

  onAlternateSelect(item: CustomMenuItem): void {
    this.selectedAlternateKey = item.value;
  }

  private loadAlternateLeaderboard(filters?: ILeaderboardFilters): void {
    if (!this.analysisId) return;
    this.leaderboardLoading = true;
    this.geotechnicalAnalysisService.getAlternateLeaderboard(this.analysisId, filters).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          if ([IAlternateLeaderboardStatus.NOT_STARTED, IAlternateLeaderboardStatus.PROCESSING].includes(res.data.status)) {
            return;
          }
          const allAlternates = res.data.alternates ?? [];
          this.rawLeaderboardAlternates = allAlternates;
          this.syncAlternateFilterItems();

          const displayed = this.applyAlternateFilter(allAlternates);
          this.alternateOptions = displayed.map((alt, i) => ({
            label: alt.key || `Alternate ${i + 1}`,
            value: alt.key,
            badgeNumber: i + 1,
          } as CustomMenuItem));
          this.leaderboardDataMap = {};
          displayed.forEach((alt) => {
            this.leaderboardDataMap[alt.key] = alt;
          });
          if (displayed.length > 0) {
            this.selectedAlternateKey = displayed[0].key;
          }
          this.buildCharts(displayed);
        }
        this.leaderboardLoading = false;
      },
      error: () => {
        this.leaderboardLoading = false;
      },
    });
  }

  private syncAlternateFilterItems(): void {
    const validKeys = new Set(this.rawLeaderboardAlternates.map(a => a.key));
    this.filterAlternateOptions = this.rawLeaderboardAlternates.map(alt => ({
      label: alt.key,
      value: alt.key,
    }));
    // On first load restore from appliedFilters; afterwards trim stale keys
    if (this.filterSelectedAlternates.length === 0 && this.appliedFilters.selectedAlternates.length > 0) {
      this.filterSelectedAlternates = this.appliedFilters.selectedAlternates.filter(k => validKeys.has(k));
    } else {
      this.filterSelectedAlternates = this.filterSelectedAlternates.filter(k => validKeys.has(k));
    }
  }

  private applyAlternateFilter(alternates: IAlternateLeaderboardItem[]): IAlternateLeaderboardItem[] {
    if (this.appliedFilters.selectedAlternates.length === 0) return alternates;
    const keys = new Set(this.appliedFilters.selectedAlternates);
    return alternates.filter(a => keys.has(a.key));
  }

  private loadLayersFromValueEngineering(): void {
    this.filterLayersLoading = true;
    this.geoReportDataService.loadValueEngineering();
  }

  private loadLayerVisualization(): void {
    if (!this.analysisId) return;
    this.layerVisualizationLoading = true;
    this.geotechnicalAnalysisService.getLayerVisualization(this.analysisId).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.layerVisualizationData = res.data;
          // Recompute filtered view and SVG cache now that raw data changed.
          this.refreshLayerVisualization();
        }
        this.layerVisualizationLoading = false;
      },
      error: () => {
        this.layerVisualizationLoading = false;
      },
    });
  }

  private loadVendorAnalysis(): void {
    if (!this.analysisId) return;
    this.geotechnicalAnalysisService.getVendorAnalysis(this.analysisId).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.geoReportDataService.setVendorAnalysis(res.data);
          const gwNames = (res.data.generalWorks ?? []).map(w => w.name?.value ?? '').filter(Boolean);
          const swNames = (res.data.stabilizationWorks ?? []).map(w => w.name?.value ?? '').filter(Boolean);
          this.generalWorksTabs = gwNames;
          this.stabilizationTabs = swNames;
          // Default: select first general works tab only
          this.selectedGeneralWorksTab = gwNames.length > 0 ? gwNames[0] : '';
          this.selectedStabilizationTab = '';
          this.buildResourceCostCharts();
          if (this.alternatesData.length > 0) {
            this.buildCharts(this.alternatesData);
          }
        }
      },
    });
  }

  onSelectResourceTab(tab: string, group: 'general' | 'stabilization'): void {
    if (group === 'general') {
      this.selectedGeneralWorksTab = tab;
      this.selectedStabilizationTab = '';
    } else {
      this.selectedStabilizationTab = tab;
      this.selectedGeneralWorksTab = '';
    }
    this.buildResourceCostCharts();
  }

  private buildResourceCostCharts(): void {
    const alternates = this.alternatives;
    if (alternates.length === 0) return;
    this.alternatesData = alternates;

    const isGeneral = !!this.selectedGeneralWorksTab;
    const selectedTab = isGeneral ? this.selectedGeneralWorksTab : this.selectedStabilizationTab;
    const keys = alternates.map(a => a.key);
    const baseGrid = { top: 16, left: 8, right: 8, bottom: 56, containLabel: true };
    const baseLegend = { bottom: 0, itemWidth: 12, itemHeight: 12, textStyle: { fontSize: 11 }, type: 'scroll' as const };
    const projectArea = this.geoReportDataService.vendorAnalysis()?.projectInfo?.projectArea?.value ?? 0;

    const getWorkItem = (alt: IAlternateLeaderboardItem) => {
      const breakdown = isGeneral ? alt.generalWorksBreakdown : alt.stabilizationWorksBreakdown;
      return (breakdown ?? []).find(w => w.workName === selectedTab);
    };

    const getUnitCost = (alt: IAlternateLeaderboardItem): number => {
      const item = getWorkItem(alt);
      if (!item || !projectArea) return 0;
      const totalWorkCost = (item.material ?? 0) + (item.labor ?? 0) + (item.equipment ?? 0);
      return Math.round((totalWorkCost / projectArea) * 100) / 100;
    };

    // Cost breakdown: Material / Labor / Equipment stacked bar per alternate
    this.costBreakdownChartOption = {
      tooltip: {
        trigger: 'axis', axisPointer: { type: 'shadow' }, confine: true,
        formatter: (params: any) => {
          const items = Array.isArray(params) ? params : [params];
          let result = `${items[0].name}<br/>`;
          items.forEach((p: any) => {
            result += `${p.marker}${p.seriesName}&nbsp;&nbsp;<b>${Number(p.value).toFixed(2)}</b><br/>`;
          });
          return result;
        },
      },
      legend: { ...baseLegend, data: ['Material', 'Labor', 'Equipment'] },
      grid: baseGrid,
      xAxis: { type: 'category', data: keys, axisLabel: { fontSize: 11 } },
      yAxis: { type: 'value', axisLabel: { formatter: (v: number) => `$${(v / 1000).toFixed(0)}k`, fontSize: 11 } },
      series: [
        {
          name: 'Material', type: 'bar', stack: 'total',
          data: alternates.map(a => getWorkItem(a)?.material ?? 0),
          itemStyle: { color: '#4472C4' },
        },
        {
          name: 'Labor', type: 'bar', stack: 'total',
          data: alternates.map(a => getWorkItem(a)?.labor ?? 0),
          itemStyle: { color: '#5B9BD5' },
        },
        {
          name: 'Equipment', type: 'bar', stack: 'total',
          data: alternates.map(a => getWorkItem(a)?.equipment ?? 0),
          itemStyle: { color: '#ED7D31' },
        },
      ],
    };

    this.unitCostByAlternatesChartOption = {
      tooltip: { trigger: 'axis', confine: true },
      legend: { ...baseLegend, data: ['Unit Cost ($/Sq.Yard)'] },
      grid: baseGrid,
      xAxis: { type: 'category', data: keys, axisLabel: { fontSize: 11 } },
      yAxis: { type: 'value', axisLabel: { formatter: (v: number) => `$${v.toFixed(2)}`, fontSize: 11 } },
      series: [
        {
          name: 'Unit Cost ($/Sq.Yard)', type: 'line',
          data: alternates.map(a => getUnitCost(a)),
          symbol: 'circle', symbolSize: 8,
          itemStyle: { color: '#1a7a6e' },
          lineStyle: { color: '#1a7a6e', width: 2 },
        },
      ],
    };
  }

  private buildCharts(alternates: IAlternateLeaderboardItem[]): void {
    if (alternates.length === 0) return;
    this.alternatives = alternates;
    this.buildResourceCostCharts();

    const keys = alternates.map(a => a.key);
    const baseGrid = { top: 16, left: 8, right: 8, bottom: 56, containLabel: true };
    const baseLegend = { bottom: 0, itemWidth: 12, itemHeight: 12, textStyle: { fontSize: 11 }, type: 'scroll' as const };

    // 3. Total cost across alternates (stacked by work name)
    const workNames = [...this.generalWorksTabs, ...this.stabilizationTabs].filter(Boolean);

    if (workNames.length > 0) {
      const totalCostSeries = workNames.map((name, i) => ({
        name,
        type: 'bar' as const,
        stack: 'total',
        data: alternates.map(alt => {
          const gw = (alt.generalWorksBreakdown ?? []).find(w => w.workName === name);
          const sw = (alt.stabilizationWorksBreakdown ?? []).find(w => w.workName === name);
          return (gw?.cost ?? 0) + (sw?.cost ?? 0);
        }),
        itemStyle: { color: CHART_COLORS[i % CHART_COLORS.length] },
      }));

      this.totalCostAcrossAlternatesChartOption = {
        tooltip: {
          trigger: 'axis', axisPointer: { type: 'shadow' }, confine: true,
          formatter: (params: any) => {
            const items = Array.isArray(params) ? params : [params];
            let result = `${items[0].name}<br/>`;
            items.forEach((p: any) => {
              result += `${p.marker}${p.seriesName}&nbsp;&nbsp;<b>${Number(p.value).toFixed(2)}</b><br/>`;
            });
            return result;
          },
        },
        legend: { ...baseLegend },
        grid: baseGrid,
        xAxis: { type: 'category', data: keys, axisLabel: { fontSize: 11 } },
        yAxis: { type: 'value', axisLabel: { formatter: (v: number) => `$${(v / 1000).toFixed(0)}k`, fontSize: 11 } },
        series: totalCostSeries,
      };
    } else {
      // Fallback: general works vs stabilization works
      this.totalCostAcrossAlternatesChartOption = {
        tooltip: {
          trigger: 'axis', axisPointer: { type: 'shadow' }, confine: true,
          formatter: (params: any) => {
            const items = Array.isArray(params) ? params : [params];
            let result = `${items[0].name}<br/>`;
            items.forEach((p: any) => {
              result += `${p.marker}${p.seriesName}&nbsp;&nbsp;<b>${Number(p.value).toFixed(2)}</b><br/>`;
            });
            return result;
          },
        },
        legend: { ...baseLegend, data: ['General Works', 'Stabilization Works'] },
        grid: baseGrid,
        xAxis: { type: 'category', data: keys, axisLabel: { fontSize: 11 } },
        yAxis: { type: 'value', axisLabel: { formatter: (v: number) => `$${(v / 1000).toFixed(0)}k`, fontSize: 11 } },
        series: [
          {
            name: 'General Works', type: 'bar', stack: 'total',
            data: alternates.map(a => a.generalWorksCost),
            itemStyle: { color: '#4472C4' },
          },
          {
            name: 'Stabilization Works', type: 'bar', stack: 'total',
            data: alternates.map(a => a.stabilizationWorksCost),
            itemStyle: { color: '#ED7D31' },
          },
        ],
      };
    }

    // 4. Unit cost across alternates (bar)
    this.unitCostAcrossAlternatesChartOption = {
      tooltip: {
        trigger: 'axis', axisPointer: { type: 'shadow' }, confine: true,
        formatter: (params: any) => {
          const items = Array.isArray(params) ? params : [params];
          let result = `${items[0].name}<br/>`;
          items.forEach((p: any) => {
            result += `${p.marker}${p.seriesName}&nbsp;&nbsp;<b>${Number(p.value).toFixed(2)}</b><br/>`;
          });
          return result;
        },
      },
      legend: { ...baseLegend, data: ['Unit Cost ($/Sq.Yard)'] },
      grid: baseGrid,
      xAxis: { type: 'category', data: keys, axisLabel: { fontSize: 11 } },
      yAxis: { type: 'value', axisLabel: { formatter: (v: number) => `$${v.toFixed(2)}`, fontSize: 11 } },
      series: [
        {
          name: 'Unit Cost ($/Sq.Yard)', type: 'bar',
          data: alternates.map(a => a.totalUnitCost),
          itemStyle: { color: '#2d9b8a' },
          barMaxWidth: 48,
        },
      ],
    };

    // 5. Schedule impact (% change vs theoretical schedule)
    const theoreticalSchedule = this.geoReportDataService.vendorAnalysis()?.projectInfo?.theoreticalScheduleWorkdays?.value ?? 0;
    this.scheduleImpactChartOption = {
      tooltip: {
        trigger: 'axis', axisPointer: { type: 'shadow' }, confine: true,
        formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params;
          return `${p.name}: ${p.value}%`;
        },
      },
      legend: { ...baseLegend, data: ['Schedule Impact (%)'] },
      grid: baseGrid,
      xAxis: { type: 'category', data: keys, axisLabel: { fontSize: 11 } },
      yAxis: { type: 'value', axisLabel: { formatter: (v: number) => `${v}%`, fontSize: 11 } },
      series: [
        {
          name: 'Schedule Impact (%)', type: 'bar',
          data: alternates.map(a => {
            if (theoreticalSchedule === 0) return 0;
            return Math.round(((a.scheduleImpact - theoreticalSchedule) / theoreticalSchedule) * 100 * 10) / 10;
          }),
          itemStyle: {
            color: (params: any) => (params.data ?? 0) < 0 ? '#EF4444' : '#2d9b8a',
          },
          barMaxWidth: 48,
        },
      ],
    };

    // 6. Cost impact (% change vs theoretical budget)
    const theoreticalBudget = this.geoReportDataService.vendorAnalysis()?.projectInfo?.theoreticalBudget?.value ?? 0;
    this.costImpactChartOption = {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, confine: true },
      legend: { ...baseLegend, data: ['Cost Impact (%)'] },
      grid: baseGrid,
      xAxis: { type: 'category', data: keys, axisLabel: { fontSize: 11 } },
      yAxis: { type: 'value', axisLabel: { formatter: (v: number) => `${v}%`, fontSize: 11 } },
      series: [
        {
          name: 'Cost Impact (%)', type: 'bar',
          data: alternates.map(a => {
            if (theoreticalBudget === 0) return 0;
            return Math.round(((a.totalCost.value - theoreticalBudget) / theoreticalBudget) * 100 * 10) / 10;
          }),
          itemStyle: { color: '#ED7D31' },
          barMaxWidth: 48,
        },
      ],
    };
  }


  // Analysis Summary methods have been moved to GeoTechnicalAnalysisSummaryTabComponent.
  // Setup click is now handled via the rightActions slot in the tab strip.

  onApplyFilter(): void {
    const enabledLayers = this.filterLayers.filter(l => l.enabled).map(l => l.label);
    const allLayersEnabled = this.filterLayers.every(l => l.enabled);
    const selectedAlternates = this.filterSelectedAlternates ?? [];

    this.appliedFilters = {
      rankBy: this.filterRankBy,
      topN: this.filterSelectedTopN,
      allLayersEnabled,
      enabledLayers,
      selectedAlternates,
    };

    // Rebuild filtered data + SVG cache so the template stays in sync.
    this.refreshLayerVisualization();

    this.loadAlternateLeaderboard({
      rankBy: this.filterRankBy,
      topN: this.filterSelectedTopN,
      layers: enabledLayers,
    });

    if (this.analysisId) {
      this.geotechnicalAnalysisService.saveDashboardFilters(this.analysisId, {
        rankBy: this.filterRankBy,
        topN: this.filterSelectedTopN,
        enabledLayers: allLayersEnabled ? [] : enabledLayers,
        selectedAlternates,
      }).subscribe();
    }
  }

  onClearFilter(): void {
    this.filterRankBy = 'cost';
    this.filterSelectedTopN = null;
    this.filterLayers.forEach(l => l.enabled = true);
    this.filterSelectedAlternates = [];
    this.appliedFilters = { rankBy: 'cost', topN: null, allLayersEnabled: true, enabledLayers: [], selectedAlternates: [] };
    // Rebuild filtered data + SVG cache after clearing filters.
    this.refreshLayerVisualization();
  }

  get hasActiveFilters(): boolean {
    return this.appliedFilters.rankBy !== 'cost'
      || this.appliedFilters.topN !== null
      || !this.appliedFilters.allLayersEnabled
      || this.appliedFilters.selectedAlternates.length > 0;
  }

  get hasFilterChanged(): boolean {
    if (this.filterRankBy !== this.appliedFilters.rankBy) return true;
    if (this.filterSelectedTopN !== this.appliedFilters.topN) return true;
    const appliedSet = new Set(this.appliedFilters.enabledLayers);
    const allApplied = this.appliedFilters.allLayersEnabled;
    if (this.filterLayers.some(l => allApplied ? !l.enabled : l.enabled !== appliedSet.has(l.label))) return true;

    // Check alternate filter changed
    const currentAlts = this.filterSelectedAlternates ?? [];
    const appliedAlts = new Set(this.appliedFilters.selectedAlternates);
    if (currentAlts.length !== appliedAlts.size || currentAlts.some(k => !appliedAlts.has(k))) return true;
    return false;
  }


  openNewAlternateDialog(): void {
    this.buildNewAlternateDialogData();
    this.showNewAlternateDialog = true;
  }

  private getMethodsForWork(workName: string): { label: string; value: string }[] {
    const rows = this.geoReportDataService.valueEngineering()?.stabilizationMethods ?? [];
    return rows
      .filter((row: IStabilizationMethodCell[]) =>
        row.find((c: IStabilizationMethodCell) => c.key === 'Stabilization Work' || c.key.startsWith('Stabilization Work '))?.value === workName
      )
      .map((row: IStabilizationMethodCell[]) => {
        const methodCell = row.find((c: IStabilizationMethodCell) => c.key === 'Stabilization Method' || c.key.startsWith('Stabilization Method '));
        return { label: methodCell?.value ?? '', value: methodCell?.value ?? '' };
      })
      .filter(m => m.value !== '');
  }


  private buildNewAlternateDialogData(): void {
    const veData = this.geoReportDataService.valueEngineering();
    if (!veData) return;

    const altData = veData.alternatives?.find(
      a => a.key === this.selectedAlternateKey
    );

    const stabMethods = veData.stabilizationMethods ?? [];
    const layerNames = this.filterLayers.map(l => l.label);

    const methodToWorkMap = new Map<string, string>();

    for (const row of stabMethods) {
      const work = row.find(c => c.key === 'Stabilization Work' || c.key.startsWith('Stabilization Work '))?.value?.trim();
      const method = row.find(c => c.key === 'Stabilization Method' || c.key.startsWith('Stabilization Method '))?.value?.trim();

      if (method && work) {
        methodToWorkMap.set(method, work);
      }
    }

    const workToMethodsMap = new Map<string, { label: string; value: string }[]>();

    for (const row of stabMethods) {
      const work = row.find(c => c.key === 'Stabilization Work' || c.key.startsWith('Stabilization Work '))?.value?.trim();
      const method = row.find(c => c.key === 'Stabilization Method' || c.key.startsWith('Stabilization Method '))?.value?.trim();

      if (!work || !method) continue;

      if (!workToMethodsMap.has(work)) {
        workToMethodsMap.set(work, []);
      }

      workToMethodsMap.get(work)!.push({
        label: method,
        value: method
      });
    }

    this.newAlternateDialogLayers = layerNames
      .map(layerName => {

        const appliedMethodNames = new Set<string>(
          (altData?.values ?? [])
            .filter(v => v.layer === layerName && v.method?.length > 0)
            .flatMap(v => v.method.map(m => m.trim()).filter(Boolean))
        );

        if (appliedMethodNames.size === 0) {
          return null;
        }

        const appliedWorkNames = new Set<string>();

        for (const methodName of appliedMethodNames) {
          const work = methodToWorkMap.get(methodName);
          if (work) {
            appliedWorkNames.add(work);
          }
        }

        const unappliedWorks = this.stabilizationTabs
          .filter(workName => !appliedWorkNames.has(workName))
          .map(workName => ({
            workName,
            open: false,
            selectedMethod: '',
            methods: (workToMethodsMap.get(workName) ?? [])
          }));

        return {
          layerName,
          open: false,
          works: unappliedWorks
        };
      })

      .filter((layer): layer is { layerName: string; open: boolean; works: any[] } => !!layer);
  }
  toggleNewAlternateLayer(index: number): void {
    this.newAlternateDialogLayers[index].open = !this.newAlternateDialogLayers[index].open;
  }

  toggleNewAlternateWork(layerIndex: number, workIndex: number): void {
    this.newAlternateDialogLayers[layerIndex].works[workIndex].open =
      !this.newAlternateDialogLayers[layerIndex].works[workIndex].open;
  }

  onSubmitNewAlternate(): void {
    const layerMethods: ICalculateCostsForNewAlternateLayerMethod[] = [];
    for (const layer of this.newAlternateDialogLayers) {
      for (const work of layer.works) {
        if (work.selectedMethod) {
          layerMethods.push({ layer: layer.layerName, parameter: work.workName, value: work.selectedMethod });
        }
      }
    }

    if (layerMethods.length === 0) {
      this.toast.warn('Please select at least one method');
      return;
    }

    this.calculatingNewAlternate.set(true);
    this.geotechnicalAnalysisService
      .calculateCostsForNewAlternate(this.analysisId, {
        currentAlternate: this.selectedAlternateKey,
        layerMethods,
      })
      .subscribe({
        next: (res) => {
          if (res.data) {
            this.toast.success('New alternate costs calculated successfully');
            this.calculatingNewAlternate.set(false);
            this.showNewAlternateDialog = false;
            this.loadAlternateLeaderboard();
            this.loadLayerVisualization();
          } else {
            this.toast.error((res as any)?.message ?? 'Failed to calculate costs');
          }
        },
        error: () => {
          this.calculatingNewAlternate.set(false);
          this.toast.error('Failed to calculate costs for new alternate');
        },
      });
  }

}
